import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type State = "loading" | "success" | "already" | "invalid" | "expired" | "error";

const NewsletterConfirm = () => {
  const { i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState<State>("loading");

  const isSv = i18n.language?.startsWith("sv");
  const copy = isSv
    ? {
        title: "Bekräfta prenumeration",
        loading: "Bekräftar din prenumeration…",
        success: "Tack! Din prenumeration är bekräftad.",
        already: "Din prenumeration är redan bekräftad.",
        invalid: "Länken är ogiltig.",
        expired: "Länken har gått ut. Anmäl dig igen för att få en ny.",
        error: "Något gick fel. Försök igen om en stund.",
        home: "Till startsidan",
        newsletter: "Tillbaka till nyhetsbrevet",
      }
    : {
        title: "Confirm your subscription",
        loading: "Confirming your subscription…",
        success: "Thanks! Your subscription is confirmed.",
        already: "Your subscription is already confirmed.",
        invalid: "This link is invalid.",
        expired: "This link has expired. Please subscribe again to get a new one.",
        error: "Something went wrong. Please try again shortly.",
        home: "Back to home",
        newsletter: "Back to newsletter",
      };

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("confirm-newsletter", {
          body: { token },
        });
        if (cancelled) return;
        if (error) { setState("error"); return; }
        if (data?.success) setState("success");
        else if (data?.reason === "already_confirmed") setState("already");
        else if (data?.reason === "expired") setState("expired");
        else setState("invalid");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const message =
    state === "loading" ? copy.loading
    : state === "success" ? copy.success
    : state === "already" ? copy.already
    : state === "expired" ? copy.expired
    : state === "invalid" ? copy.invalid
    : copy.error;

  const icon =
    state === "loading" ? <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    : state === "success" || state === "already" ? <CheckCircle2 className="w-8 h-8 text-primary" />
    : <AlertCircle className="w-8 h-8 text-destructive" />;

  return (
    <>
      <SeoHead title={copy.title} description={copy.title} path={isSv ? "/sv/newsletter/confirm" : "/newsletter/confirm"} noIndex />
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1">
          <Section title={copy.title}>
            <div className="max-w-xl mx-auto text-center space-y-6">
              <div className="flex justify-center">{icon}</div>
              <p className="text-lg text-foreground">{message}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button asChild variant="outline">
                  <Link to={isSv ? "/sv" : "/"}>{copy.home}</Link>
                </Button>
                <Button asChild>
                  <Link to={isSv ? "/sv/newsletter" : "/newsletter"}>{copy.newsletter}</Link>
                </Button>
              </div>
            </div>
          </Section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default NewsletterConfirm;
